// TokensTab — the profile hub's Personal access tokens surface (ADR-004).
// List the caller's own tokens; Generate opens the create drawer → the show-once
// SecretDialog; each row can be renamed (a one-field DialogForm) or revoked
// (ConfirmDialog that shows last-used so a live token gives pause). Read-only
// credentials: the list never exposes a hash — only the `emsp_…last4` hint.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RenamePatRequestSchema, type RenamePatRequest } from '@ems-app/schemas/api-keys/requests';

import {
  usePersonalAccessTokens,
  useCreatePat,
  useRenamePat,
  useRevokePat,
} from '../../hooks/usePersonalAccessTokens.js';
import { DataTable, type DataTableColumn } from '../../components/ui/DataTable.js';
import { CreateButton } from '../../components/patterns/CreateButton.js';
import { Badge } from '../../components/ui/Badge.js';
import { Menu, MenuItem } from '../../components/ui/Menu.js';
import { IconButton } from '../../components/ui/IconButton.js';
import { icons } from '../../components/ui/icons.js';
import { DialogForm } from '../../components/patterns/forms/DialogForm.js';
import { useFormShell, type StampedRow } from '../../components/patterns/forms/useFormShell.js';
import { Input } from '../../components/ui/Input.js';
import { Label } from '../../components/ui/Label.js';
import { Alert } from '../../components/ui/Alert.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { SecretDialog } from '../../components/patterns/SecretDialog.js';
import { useToast } from '../../components/ui/Toast.js';
import { formatDate } from '../../lib/format.js';
import { useDisplayZone } from '../../hooks/useDisplayZone.js';
import type { PersonalAccessToken } from '@ems-app/schemas/api-keys/responses';
import type { CreatePatRequest } from '@ems-app/schemas/api-keys/requests';
import { PatFormDrawer, type PatSubmit } from './PatFormDrawer.js';

type Lifecycle = 'active' | 'expired' | 'revoked';
function lifecycle(p: PersonalAccessToken): Lifecycle {
  if (p.revoked_at) return 'revoked';
  if (p.expires_at && new Date(p.expires_at) <= new Date()) return 'expired';
  return 'active';
}

// The rename — one field, the label bounded as the wire's is. A person's own token has one writer: its read carries
// no stamp, so the save sends none and a refusal is the code's words (the dialog's line, never a lost rejection).
type TokenRow = Pick<PersonalAccessToken, 'label'> & StampedRow;

function RenamePatDialog({ token, onClose }: { token: PersonalAccessToken | null; onClose: () => void }) {
  const { t } = useTranslation('tokens');
  const toast = useToast();
  const renamePat = useRenamePat();
  const shell = useFormShell<RenamePatRequest, TokenRow>({
    schema: RenamePatRequestSchema,
    row: token,
    values: (r) => ({ label: r?.label ?? '' }),
    onSubmit: async (v) => {
      if (!token) return;
      await renamePat.mutateAsync({ id: token.id, body: { label: v.label } });
    },
    onSuccess: () => {
      toast.success(t('rename.done'));
      onClose();
    },
  });
  return (
    <DialogForm shell={shell} open={token != null} onClose={onClose} title={t('rename.title')} submitLabel={t('rename.save')}>
      <div>
        <Label htmlFor="pat-rename">{t('create.label')}</Label>
        <Input id="pat-rename" {...shell.form.register('label')} maxLength={64} error={shell.form.formState.errors.label ? ' ' : undefined} />
      </div>
    </DialogForm>
  );
}

export function TokensTab() {
  const { t } = useTranslation('tokens');
  const { t: tc } = useTranslation('common');
  // The screen's one zone (ADR-082 §2): the viewer's own — a platform / personal page.
  const display = useDisplayZone({ kind: 'person' });
  const zone = display.zone ?? 'UTC';
  const toast = useToast();

  const tokens = usePersonalAccessTokens();
  const createPat = useCreatePat();
  const revokePat = useRevokePat();

  const [createOpen, setCreateOpen] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const [renameTarget, setRenameTarget] = useState<PersonalAccessToken | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<PersonalAccessToken | null>(null);

  const statusBadge = (p: PersonalAccessToken) => {
    const s = lifecycle(p);
    if (s === 'revoked') return <Badge variant="danger" appearance="bold">{t('status.revoked')}</Badge>;
    if (s === 'expired') return <Badge variant="neutral" appearance="subtle">{t('status.expired')}</Badge>;
    return <Badge variant="success" appearance="subtle">{t('status.active')}</Badge>;
  };

  const columns: DataTableColumn<PersonalAccessToken>[] = [
    { id: 'label', header: t('list.cols.label'), sortable: true, value: (p) => p.label, cell: (p) => <span className="font-medium">{p.label}</span> },
    { id: 'project', header: t('list.cols.project'), value: (p) => p.project_name ?? '', cell: (p) => <span>{p.project_name ?? '—'}</span> },
    {
      id: 'permissions',
      header: t('list.cols.permissions'),
      cell: (p) => <Badge variant="neutral" appearance="subtle">{p.permissions.length}</Badge>,
      dropOrder: 40,
    },
    {
      id: 'key',
      header: t('list.cols.key'),
      cell: (p) => <span className="font-mono text-xs text-[var(--color-text-secondary)]">{p.key_hint ? `emsp_…${p.key_hint}` : '—'}</span>,
      dropOrder: 30,
    },
    { id: 'status', header: t('list.cols.status'), sortable: true, value: (p) => lifecycle(p), cell: statusBadge },
    {
      id: 'expires',
      header: t('list.cols.expires'),
      value: (p) => p.expires_at ?? '',
      cell: (p) => <span className="text-sm">{p.expires_at ? formatDate(p.expires_at, 'date', { timeZone: zone }) : t('neverExpires')}</span>,
      dropOrder: 20,
    },
    {
      id: 'lastUsed',
      header: t('list.cols.lastUsed'),
      sortable: true,
      value: (p) => p.last_used_at ?? '',
      cell: (p) =>
        p.last_used_at ? (
          <span className="text-sm">{formatDate(p.last_used_at, 'datetime', { timeZone: zone })}</span>
        ) : (
          <span className="text-sm text-[var(--color-text-tertiary)]">{t('neverUsed')}</span>
        ),
      dropOrder: 20,
    },
    {
      // An action column's header must still exist for AT (axe empty-table-header).
      id: 'actions',
      header: <span className="sr-only">{tc('actions.menu')}</span>,
      neverDrop: true,
      minWidth: '3.5rem',
      cell: (p) => (
        <Menu
          label={t('list.rowActions', { label: p.label })}
          placement="bottom end"
          renderTrigger={() => (
            <IconButton variant="ghost" aria-label={t('list.rowActions', { label: p.label })}>
              <icons.more size={18} aria-hidden />
            </IconButton>
          )}
        >
          <MenuItem onAction={() => setRenameTarget(p)}>
            {t('actions.rename')}
          </MenuItem>
          {lifecycle(p) !== 'revoked' && (
            <MenuItem tone="danger" onAction={() => setRevokeTarget(p)}>
              {t('actions.revoke')}
            </MenuItem>
          )}
        </Menu>
      ),
    },
  ];

  const create = <CreateButton label={t('actions.generate')} onClick={() => setCreateOpen(true)} />;

  async function handleCreate(body: PatSubmit): Promise<void> {
    // The picker offers only PAT_GRANTABLE_KEYS, so the string[] is the read-tier
    // enum at the wire boundary (the server re-validates via Zod regardless).
    const res = await createPat.mutateAsync({
      label: body.label,
      project_id: body.project_id,
      permissions: body.permissions as CreatePatRequest['permissions'],
      expires_in: body.expires_in,
    });
    setSecret(res.secret);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-[var(--color-text-secondary)] max-w-2xl">{t('intro')}</p>
        <div className="shrink-0">{create}</div>
      </div>

      <DataTable
        columns={columns}
        data={tokens.data}
        getRowId={(p) => p.id}
        caption={t('list.tableCaption')}
        isPending={tokens.isPending}
        isFetching={tokens.isFetching}
        isError={tokens.isError}
        errorMessage={t('list.error')}
        onRetry={() => void tokens.refetch()}
        empty={
          <EmptyState
            icon={<icons.token size={48} />}
            title={t('list.empty.title')}
            description={t('list.empty.body')}
            action={create}
          />
        }
      />

      <PatFormDrawer
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        onSuccess={() => {
          toast.success(t('create.created'));
          setCreateOpen(false);
        }}
      />

      <SecretDialog
        open={secret != null}
        secret={secret ?? ''}
        onClose={() => setSecret(null)}
        title={t('secret.title')}
        warning={t('secret.body')}
        fieldLabel={t('secret.label')}
        doneLabel={t('secret.done')}
      />

      {/* Rename — a single-field dialog (cosmetic; nothing the credential can do changes). */}
      <RenamePatDialog token={renameTarget} onClose={() => setRenameTarget(null)} />

      <ConfirmDialog
        open={revokeTarget != null}
        onClose={() => setRevokeTarget(null)}
        tone="danger"
        title={t('revoke.title')}
        description={t('revoke.body')}
        confirmLabel={t('revoke.confirm')}
        onConfirm={async () => {
          if (!revokeTarget) return;
          await revokePat.mutateAsync(revokeTarget.id);
          toast.success(t('revoke.done'));
          setRevokeTarget(null);
        }}
      >
        {revokeTarget?.last_used_at && (
          <Alert variant="warning">{t('revoke.lastUsedWarn', { when: formatDate(revokeTarget.last_used_at, 'datetime', { timeZone: zone }) })}</Alert>
        )}
      </ConfirmDialog>
    </div>
  );
}

export default TokensTab;
